import ChevronRight from "@mui/icons-material/ChevronRight";
import { Button, Skeleton, ButtonProps, Tab, Tabs, useTheme } from "@mui/material";
import { useAlerts } from "components/Alerts/AlertProvider";
import { ConfirmDialog } from "components/ConfirmDialog";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { DocumentReviewStatus, getDocumentReviewStatusLabel } from "util/documentReviewStatus";

import { DocumentDialogContainer } from "../shared";
import { DocumentDetailsSection } from "./components/DocumentDetailsSection";
import { DocumentMetadataFormSectionsSkeleton } from "./components/DocumentMetadataFormSectionsSkeleton";
import { DocumentSwitcherSelect } from "./components/DocumentSwitcherSelect";
import { ManifestTabSection } from "./components/ManifestTabSection";
import { RelatedTasksSection, RelatedTasksTabHeader } from "./components/RelatedTasksSection";
import { TabPanel } from "./components/TabPanel";
import { useDocumentDetailsForm } from "./hooks/useDocumentDetailsForm";
import { useDocumentMetadata } from "./hooks/useDocumentMetadata";
import { useManifestReviewSection } from "./hooks/useManifestReviewSection";
import {
  REVIEW_TABS,
  ReviewDocumentMetadataDialogProps,
  ReviewTab,
  ReviewTabValue,
  TabSectionHandle,
} from "./types";

export function ReviewDocumentMetadataDialog({
  open,
  onClose,
  documentIds,
  tenantId,
  onComplete,
  title,
  hasDocumentAI = true,
  onCurrentDocumentChange,
  wasteManifestId: initialWasteManifestId,
}: ReviewDocumentMetadataDialogProps) {
  const theme = useTheme();
  const alerts = useAlerts();
  const { isStaff } = useCurrentUser();
  const { featureFlags } = useFeatureFlags();
  const canShowManifestReview = isStaff && !!featureFlags[FeatureFlagKey.ManifestDocumentIngest];
  const [currentDocumentId, setCurrentDocumentId] = useState<string | undefined>(
    () => documentIds[0]
  );
  const [currentTab, setCurrentTab] = useState<ReviewTabValue>(
    initialWasteManifestId ? "manifest-details" : "document-details"
  );

  const [confirmSkipDialogOpen, setConfirmSkipDialogOpen] = useState(false);
  const openConfirmSkipDialog = useCallback(() => setConfirmSkipDialogOpen(true), []);
  const closeConfirmSkipDialog = useCallback(() => setConfirmSkipDialogOpen(false), []);

  const [confirmCloseDialogOpen, setConfirmCloseDialogOpen] = useState(false);
  const openConfirmCloseDialog = useCallback(() => setConfirmCloseDialogOpen(true), []);
  const closeConfirmCloseDialog = useCallback(() => setConfirmCloseDialogOpen(false), []);

  const currentIndex = useMemo(() => {
    if (!currentDocumentId) {
      return 0;
    }
    const index = documentIds.indexOf(currentDocumentId);
    return index >= 0 ? index : 0;
  }, [currentDocumentId, documentIds]);

  const hasNext = currentIndex < documentIds.length - 1;
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const onDocumentChange = useCallback(
    (documentId: string) => {
      onCurrentDocumentChange?.(documentId);
      scrollContainerRef.current?.scrollTo({ top: 0, behavior: "smooth" });
    },
    [onCurrentDocumentChange]
  );

  useEffect(() => {
    if (documentIds.length === 0) {
      setCurrentDocumentId(undefined);
      return;
    }
    setCurrentDocumentId((prev) => {
      if (prev && documentIds.includes(prev)) return prev;
      return documentIds[0];
    });
  }, [documentIds]);

  const navigateToDocument = useCallback(
    (documentId: string) => {
      setCurrentTab(REVIEW_TABS.DOCUMENT_DETAILS);
      setCurrentDocumentId(documentId);
      onDocumentChange(documentId);
    },
    [onDocumentChange]
  );

  const handleSelectDocument = useCallback(
    (documentId: string) => {
      if (!documentId || documentId === currentDocumentId) return;
      navigateToDocument(documentId);
    },
    [currentDocumentId, navigateToDocument]
  );

  const handleTabChange = useCallback((_: React.SyntheticEvent, value: ReviewTabValue) => {
    setCurrentTab(value);
  }, []);

  const {
    document,
    metadata,
    loading,
    suggestedFacilities,
    suggestedFacilitiesLoading,
    previewUrl,
  } = useDocumentMetadata({
    documentId: currentDocumentId,
    open,
    hasDocumentAI,
  });

  const form = useDocumentDetailsForm({
    documentId: currentDocumentId,
    document,
    metadata,
    tenantId,
  });

  const resolvedWasteManifestId =
    initialWasteManifestId ?? document?.sourceWasteManifestId ?? undefined;

  const manifest = useManifestReviewSection({
    canShowManifestReview,
    wasteManifestId: resolvedWasteManifestId,
    documentId: currentDocumentId,
    onClose,
  });

  const needsReview = useMemo(() => {
    return !!document?.documentMetadata?.needsReview || manifest.manifestNeedsReview;
  }, [document, manifest.manifestNeedsReview]);

  const hasRelatedTasks = (document?.tasks ?? []).length > 0;

  const tabs = useMemo((): ReviewTab[] => {
    const result: ReviewTab[] = [
      { value: REVIEW_TABS.DOCUMENT_DETAILS, label: "Document Details", showPreview: true },
    ];

    if (hasRelatedTasks) {
      result.push({
        value: REVIEW_TABS.RELATED_TASKS,
        label: <RelatedTasksTabHeader document={document} />,
        showPreview: false,
      });
    }

    if ((form.isRCRAManifest || resolvedWasteManifestId) && canShowManifestReview) {
      result.push({
        value: REVIEW_TABS.MANIFEST_DETAILS,
        label: "Manifest Details",
        showPreview: true,
      });
    }

    return result;
  }, [
    document,
    hasRelatedTasks,
    form.isRCRAManifest,
    resolvedWasteManifestId,
    canShowManifestReview,
  ]);

  const showTabs = tabs.length > 1;
  const activeTab = tabs.find((t) => t.value === currentTab) ?? tabs[0];
  const effectiveTab = activeTab?.value ?? REVIEW_TABS.DOCUMENT_DETAILS;
  const showPreview = activeTab?.showPreview ?? true;

  const isSaving = loading || form.saving || manifest.manifestSaving;

  const detailsRef = useRef<TabSectionHandle>(null);
  const manifestRef = useRef<TabSectionHandle>(null);

  const handleSaveAndNext = useCallback(async () => {
    try {
      if (!currentDocumentId) {
        alerts.error("Document not selected");
        return;
      }

      let didSave = false;

      // Save document details (validates form, then saves)
      if (detailsRef.current) {
        const saved = await detailsRef.current.save();
        if (saved) didSave = true;
      }

      // Save manifest data
      if (manifestRef.current) {
        try {
          const saved = await manifestRef.current.save();
          if (saved) didSave = true;
        } catch (err) {
          alerts.error("Failed to save manifest data", err);
          return;
        }
      }

      if (didSave) {
        alerts.success("Document and metadata saved successfully");
      }

      if (hasNext) {
        navigateToDocument(documentIds[currentIndex + 1]);
      } else {
        onComplete?.();
        onClose();
      }
    } catch (error) {
      alerts.error("Failed to save document and metadata", error);
    }
  }, [
    currentDocumentId,
    currentIndex,
    hasNext,
    documentIds,
    alerts,
    onComplete,
    onClose,
    navigateToDocument,
  ]);

  const handleSkipWithoutReviewing = useCallback(() => {
    if (hasNext) {
      navigateToDocument(documentIds[currentIndex + 1]);
      closeConfirmSkipDialog();
    }
  }, [hasNext, currentIndex, documentIds, navigateToDocument, closeConfirmSkipDialog]);

  const dialogTitle = useMemo(() => {
    if (title) return title;

    if (hasDocumentAI && (metadata?.needsReview || manifest.manifestNeedsReview)) {
      return "Review & Confirm Details";
    }
    return "Edit Document";
  }, [title, hasDocumentAI, metadata?.needsReview, manifest.manifestNeedsReview]);

  const handleClose = useMemo(
    () => (needsReview ? openConfirmCloseDialog : onClose),
    [needsReview, openConfirmCloseDialog, onClose]
  );

  if (!open) return null;

  return (
    <>
      <DocumentDialogContainer
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth={"lg"}
        title={dialogTitle}
        sx={{
          "& .MuiDialog-paper": {
            height: "960px", // fixed height so the dialog can scroll internally
          },
        }}
        contentSx={{ pt: 0 }}
        stickyHeaderContent={
          showTabs && !loading && document ? (
            <Tabs value={effectiveTab} onChange={handleTabChange} sx={{ mb: 1 }}>
              {tabs.map((tab) => (
                <Tab key={tab.value} label={tab.label} value={tab.value} />
              ))}
            </Tabs>
          ) : undefined
        }
        formContainerProps={{
          ref: scrollContainerRef,
        }}
        previewUrl={showPreview ? previewUrl : undefined}
        fileExtension={document?.fileExtension ?? undefined}
        previewLoading={loading}
        showPreviewBoxIfFailed={showPreview}
        formColumnSize={{ xs: showPreview ? 6 : 12 }}
        previewColumnSize={{ xs: showPreview ? 6 : 0 }}
        formContainerSx={{ maxHeight: "calc(960px - 140px)" }}
        secondaryActionElements={[
          <ButtonWithSkeleton
            key="close"
            onClick={handleClose}
            disabled={loading}
            loading={loading}
            size="medium"
            variant="text"
            sx={{ color: theme.palette.text.secondary }}
          >
            {needsReview ? "Close Without Reviewing" : "Close"}
          </ButtonWithSkeleton>,
        ]}
        additionalPrimaryActionElements={
          documentIds.length > 1 && hasNext
            ? [
                <ButtonWithSkeleton
                  key="skip-without-reviewing"
                  onClick={openConfirmSkipDialog}
                  disabled={!hasNext || loading}
                  loading={loading}
                  size="medium"
                  variant="text"
                  sx={{ color: theme.palette.text.secondary }}
                >
                  Skip
                </ButtonWithSkeleton>,
              ]
            : []
        }
        primaryActionButton={
          <ButtonWithSkeleton
            key="primary-action-button"
            loading={isSaving}
            disabled={isSaving}
            variant="contained"
            onClick={handleSaveAndNext}
            endIcon={hasNext ? <ChevronRight /> : undefined}
          >
            {getPrimaryButtonLabel(needsReview, hasNext)}
          </ButtonWithSkeleton>
        }
        noPrimaryAction={false}
        hideCancelButton={true}
        titleBarActionElement={
          documentIds.length > 1 ? (
            <DocumentSwitcherSelect
              documentIds={documentIds}
              tenantId={tenantId}
              selectedId={currentDocumentId}
              skip={!open}
              disabled={documentIds.length <= 1}
              onSelect={handleSelectDocument}
            />
          ) : null
        }
      >
        <>
          {loading || !document ? (
            <DocumentMetadataFormSectionsSkeleton />
          ) : (
            <>
              <TabPanel active={effectiveTab === REVIEW_TABS.DOCUMENT_DETAILS}>
                <DocumentDetailsSection
                  ref={detailsRef}
                  form={form}
                  document={document}
                  suggestedFacilities={suggestedFacilities}
                  suggestedFacilitiesLoading={suggestedFacilitiesLoading}
                  tenantId={tenantId}
                />
              </TabPanel>

              <TabPanel active={effectiveTab === REVIEW_TABS.RELATED_TASKS}>
                <RelatedTasksSection document={document} />
              </TabPanel>

              {showTabs && (
                <TabPanel active={effectiveTab === REVIEW_TABS.MANIFEST_DETAILS}>
                  <ManifestTabSection
                    ref={manifestRef}
                    manifest={manifest}
                    documentId={currentDocumentId}
                    documentTenantId={document?.tenantId}
                    documentType={form.currentDocumentType}
                    canShowManifestReview={canShowManifestReview}
                    tenantId={tenantId}
                  />
                </TabPanel>
              )}
            </>
          )}
        </>
      </DocumentDialogContainer>
      <ConfirmDialog
        open={confirmSkipDialogOpen}
        onClose={closeConfirmSkipDialog}
        onConfirm={handleSkipWithoutReviewing}
        header="Are you sure you want to skip review?"
        info={`You can revisit and review any time in the future, but this document will be labeled as ${getDocumentReviewStatusLabel(DocumentReviewStatus.NeedsReview)}.`}
        confirmText="Yes, Next Document"
      />
      <ConfirmDialog
        open={confirmCloseDialogOpen}
        onClose={closeConfirmCloseDialog}
        onConfirm={onClose}
        header={`Are you sure you want to skip reviewing ${documentIds.length > 1 ? "all unreviewed documents" : "this document"}?`}
        info={`You can revisit and review any time in the future, but ${documentIds.length > 1 ? "all unreviewed documents" : "this document"} will be labeled as ${getDocumentReviewStatusLabel(DocumentReviewStatus.NeedsReview)}.`}
        confirmText="Yes, Skip & Close"
      />
    </>
  );
}

function getPrimaryButtonLabel(needsReview: boolean, hasNext: boolean): string {
  if (hasNext) {
    return needsReview ? "Confirm Details & Next" : "Save & Next";
  }
  return needsReview ? "Confirm Details" : "Save";
}

/**
 *  A simple wrapper around the Button component that shows a skeleton when the loading prop is true.
 */
const ButtonWithSkeleton = <T extends ButtonProps>(props: T & { loading: boolean }) => {
  const { loading, key, ...rest } = props;
  if (loading) {
    return <Skeleton key={key} width={100} height={32} />;
  }
  return <Button {...rest} />;
};
